/**
 * The dialog shell both rep surfaces start work in (DG.33).
 *
 * ## A real `<dialog>`, opened by its attribute and never by `showModal()`
 *
 * The element is what a dialog is, so a div wearing `role="dialog"` throws away everything the platform
 * knows about one for nothing. `showModal()` is the half that cannot be used: it puts the element in the
 * top layer, positioned against the viewport, which is exactly right for the standalone application and
 * exactly wrong for the widget. That frame is sized to its own content by `iqz-ddds:resize`, so its
 * viewport *is* the whole frame - a centred dialog in a 1200px frame lands 600px down, below the fold of
 * whatever the CRM is showing, and the host cannot be asked to scroll without adding a message to section
 * 14's protocol.
 *
 * So the embedded shape is a takeover in normal flow. `ExchangeView` hides the panels, this takes their
 * place, and the frame resizes to the form - which puts the dialog inside what the rep can already see,
 * with no host cooperation at all. `surface.ts` undoes the user agent's positioning for both shapes.
 *
 * ## What that leaves hand-rolled, and is therefore worth reading carefully
 *
 * Focus in on open, focus back to the trigger on close, Escape, and a Tab trap on the overlay - all of
 * which `showModal()` would have given for free, and none of which it can give here. It shows up in no
 * screenshot and it is what somebody using a keyboard depends on; `Modal.spec.tsx` pins each one.
 *
 * A click on the scrim does **not** close. That is deliberate rather than an omission: this dialog holds
 * work that has not been committed - four files a rep has just picked, three labels they have just typed -
 * and the conventional dismiss-on-backdrop throws all of it away on a stray click at the edge of a form.
 */
import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';

export interface ModalProps {
  /** The heading, and the dialog's accessible name. One string, so the two cannot drift apart. */
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /** The actions. Rendered below the body, where a person looks for them last. */
  readonly footer?: ReactNode;
  /**
   * `overlay` floats above the page; `takeover` sits in the flow where the panel was.
   *
   * The widget passes `takeover` because its frame has no viewport of its own to centre against. See
   * the file comment - this is a geometry constraint rather than a preference.
   */
  readonly variant?: 'overlay' | 'takeover';
}

/** Everything inside `root` that a Tab would stop on, in document order. */
function focusable(root: HTMLElement): readonly HTMLElement[] {
  const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return [...root.querySelectorAll<HTMLElement>(selector)];
}

/**
 * Wrap focus at the edges of `stops`, and nowhere else.
 *
 * Returns whether it moved, so the caller knows whether to swallow the keystroke. Intercepting every
 * Tab would mean reimplementing the browser's own ordering, which is both more code and worse.
 */
function wrapFocus(stops: readonly HTMLElement[], backwards: boolean): boolean {
  const first = stops[0];
  const last = stops.at(-1);
  if (first === undefined || last === undefined) return false;

  if (!backwards && document.activeElement === last) {
    first.focus();
    return true;
  }
  if (backwards && document.activeElement === first) {
    last.focus();
    return true;
  }
  return false;
}

export function Modal({ title, onClose, children, footer, variant = 'overlay' }: ModalProps): React.JSX.Element {
  const headingId = useId();
  const dialog = useRef<HTMLDialogElement>(null);

  /*
   * `onClose` read through a ref, so the key listener below is attached once.
   *
   * A caller passing an inline arrow would otherwise tear the listener down and rebuild it on every
   * render, and Escape pressed in the gap would do nothing. The same reasoning as `onActionRef` in the
   * widget's host session, for the same reason.
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  /*
   * Where focus was before this opened, so it can be given back.
   *
   * A ref captured during the mount effect rather than during render: React may render a component more
   * than once before committing it, and the second pass would record the dialog itself.
   */
  const returnTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.focus();

    return () => {
      /*
       * Only if it is still there. The widget's case with `chrome: 'none'`: the button that opened this
       * is in the host page, so `returnTo` is either null or an element that has since been removed -
       * and `focus()` on a detached node silently does nothing, which is the right outcome but only
       * because it is checked rather than assumed.
       */
      const previous = returnTo.current;
      if (previous?.isConnected === true) previous.focus();
    };
  }, []);

  /*
   * Escape and the Tab trap, on the document rather than on the element.
   *
   * Two reasons, and the second is the load-bearing one. A `<dialog>` is not an interactive element, so
   * a keyboard handler on it is a defect a linter is right to report - the browser has no built-in
   * behaviour there to extend. And a listener on the document keeps working when focus is somewhere the
   * key would not bubble from, which for a hand-rolled dialog is the case worth surviving.
   */
  useEffect(() => {
    function onKeyDown(event: globalThis.KeyboardEvent): void {
      const root = dialog.current;
      if (root === null) return;

      if (event.key === 'Escape') {
        closeRef.current();
        return;
      }

      // Only the overlay traps: under `takeover` the panels are hidden, so there is nothing behind to
      // tab into and the browser's own order is already correct.
      if (event.key !== 'Tab' || variant !== 'overlay') return;
      if (!root.contains(document.activeElement)) return;

      if (wrapFocus(focusable(root), event.shiftKey)) event.preventDefault();
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [variant]);

  /*
   * A real `<dialog>`, and deliberately not `showModal()`.
   *
   * The element is what a dialog is, and using a div with `role="dialog"` throws away everything the
   * platform knows about one. `showModal()` is the half that cannot be used: it puts the element in the
   * top layer, positioned against the viewport, and the widget's frame is sized to its own content -
   * so the viewport *is* the frame and a centred dialog lands below the fold of what the CRM is
   * showing. Kept open by the attribute instead, which leaves the geometry to `surface.ts`.
   */
  const panel = (
    <dialog
      className={`ddds-modal${variant === 'takeover' ? ' ddds-modal--takeover' : ''}`}
      ref={dialog}
      open
      aria-modal={variant === 'overlay'}
      aria-labelledby={headingId}
      // Focusable so the dialog itself can hold focus on open, but never a Tab stop.
      tabIndex={-1}
    >
      <div className="ddds-modal__head">
        <h2 className="ddds-modal__title" id={headingId}>
          {title}
        </h2>
        <button className="ddds-modal__close" type="button" onClick={onClose} aria-label="Close">
          <span aria-hidden="true">&#10005;</span>
        </button>
      </div>

      <div className="ddds-modal__body">{children}</div>

      {footer !== undefined && <div className="ddds-modal__foot">{footer}</div>}
    </dialog>
  );

  // No handler on the scrim. See the file comment: dismissing on a backdrop click would discard staged work.
  return variant === 'takeover' ? panel : <div className="ddds-modal__scrim">{panel}</div>;
}
